import _isNil from 'lodash/isNil';
import _min from 'lodash/min';
import _max from 'lodash/max';
import isNumber from 'lodash/isNumber';
import {
  ConfiguredRange,
  Range,
  MeasurementDimension,
  Visualization,
  DimensionWithStats,
} from '../types';
import { SubjectStats } from './buildQueries';
import { HasDataSignature } from '../NoDataGaurd';
import { ResponseStats } from '../useQueriedData';
import { FormatMessage } from '../../../../../util/intl';

export type CalculatedRanges = {
  optimal: Range;
  normal: Range;
  absolute: Range;
};

export const defaultColors = {
  optimal: '#8CC654',
  normal: '#AAE3F8',
  bad: '#C31533',
  data: '#660AB7',
};

export const isValidVisualization = (
  dimensions: any[],
  visualization: Visualization,
) => {
  switch (visualization) {
    case 'range':
      return true;
    case 'cartesian':
      return dimensions.length === 2 || dimensions.length === 3;
    case 'ratio':
      return dimensions.length === 2;
    case 'polar':
      return dimensions.length >= 3;
    default:
      return false;
  }
};

export const pairConfigAndData = (
  dimensions: MeasurementDimension[],
  responses: ResponseStats[],
): DimensionWithStats[] => {
  return (dimensions || []).map(dimension => {
    const responseStats = (responses || []).find(
      response => response.id === dimension.id,
    );
    return {
      id: dimension.id,
      dimension,
      responseStats,
    } as DimensionWithStats;
  });
};

const safeGetConfiguredRange = (
  configuredRange: ConfiguredRange,
  fallbackRange?: Range,
): Range => {
  const returnVal = {
    lower: configuredRange?.isRangeEnabled
      ? configuredRange?.range?.lower
      : null,
    upper: configuredRange?.isRangeEnabled
      ? configuredRange.range?.upper
      : null,
  };

  if (fallbackRange) {
    if (returnVal.lower === null && fallbackRange?.lower !== null) {
      returnVal.lower = fallbackRange.lower;
    }

    if (returnVal.upper === null && fallbackRange?.upper !== null) {
      returnVal.upper = fallbackRange.upper;
    }
  }

  return returnVal;
};

export const isRangeValid = (range: Range): boolean =>
  !!range &&
  !_isNil(range?.lower) &&
  !_isNil(range?.upper) &&
  range.lower <= range.upper;

export const getCalculatedRange = (
  dimension: MeasurementDimension,
  value?: number | string | null,
): CalculatedRanges => {
  const optimal = safeGetConfiguredRange(dimension?.optimalRange);
  const normal = safeGetConfiguredRange(dimension?.normalRange);
  const absolute = safeGetConfiguredRange(dimension?.absoluteRange);

  // take the value and add 10%, so we can show the actual value within the range
  // when visually displaying the ranges. Otherwise the ranges are shown, but the
  // actual values are not displayed since they are outside the range

  const upperValue = isNumber(value) ? Math.ceil(value + value * 0.1) : null;
  const lowerValue = isNumber(value) ? Math.floor(value - value * 0.1) : null;

  const minOfBounds = _min(
    [optimal?.lower, normal?.lower, absolute?.lower, lowerValue].filter(
      _ => !_isNil(_),
    ),
  );
  const maxOfBounds = _max(
    [optimal?.upper, normal?.upper, absolute?.upper, upperValue].filter(
      _ => !_isNil(_),
    ),
  );

  if (!_isNil(minOfBounds)) absolute.lower = minOfBounds;

  if (!_isNil(maxOfBounds)) absolute.upper = maxOfBounds;

  return {
    optimal,
    normal,
    absolute,
  };
};

export const isWithinRange = (range: Range, value: number | string) => {
  if (!isRangeValid(range) || _isNil(value) || !isNumber(value)) return false;

  // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
  return value >= range.lower && value <= range.upper;
};

export const extractConfiguredValue = (
  dimension: MeasurementDimension,
  subjectStats: SubjectStats,
) => {
  switch (dimension?.sampling) {
    case 'avg':
      return subjectStats?.avg;
    case 'min':
      return subjectStats?.min;
    case 'max':
      return subjectStats?.max;
    case 'first':
      return subjectStats?.first;
    case 'last':
      return subjectStats?.last;
    default:
      return null;
  }
};

export const singleDimensionDataCheck =
  (index: number): HasDataSignature =>
  (pairedDimensions: DimensionWithStats[]) => {
    if (pairedDimensions?.[index]?.responseStats?.hasError) return false;

    const configuredValue = extractConfiguredValue(
      pairedDimensions[index].dimension,
      pairedDimensions[index].responseStats.subjectStats,
    );

    return !_isNil(configuredValue);
  };

export const multiDimensionCheck: HasDataSignature = (
  pairedDimensions: DimensionWithStats[],
) => {
  return pairedDimensions.every((_, index) =>
    singleDimensionDataCheck(index)(pairedDimensions),
  );
};

export const shouldNormalizePolarData = (
  pairedDimensions: DimensionWithStats[],
): boolean => {
  // all polar coordinates must have the same absolute bounds to not be normalized
  const calculatedRanges = pairedDimensions.map(_ =>
    getCalculatedRange(_.dimension),
  );

  const firstAbsLower = calculatedRanges[0].absolute.lower;
  const firstAbsUpper = calculatedRanges[0].absolute.upper;

  const shouldNormalize =
    !calculatedRanges.every(_ => _.absolute.lower === firstAbsLower) ||
    !calculatedRanges.every(_ => _.absolute.upper === firstAbsUpper);

  return shouldNormalize;
};

export const maybeNormalizePolarValue = (
  pairedDimensions: DimensionWithStats[],
  dimensionId: string,
  value: number,
): number => {
  if (!shouldNormalizePolarData(pairedDimensions)) return value;

  const targetDimension = pairedDimensions.find(_ => _.id === dimensionId);
  const targetCalculatedRange = getCalculatedRange(
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    targetDimension.dimension,
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    targetDimension.responseStats.projectStats,
  );
  return (
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    (value - targetCalculatedRange.absolute.lower) /
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    (targetCalculatedRange.absolute.upper -
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      targetCalculatedRange.absolute.lower)
  );
};

export const prettyPrintNum = (value: number | string) => {
  if (_isNil(value)) return '';
  if (typeof value === 'string') return value;

  return parseFloat(value.toFixed(4)).toString();
};

export const extractValueDate = ({
  dimension,
  formatMessage,
  subjectStats,
}: {
  dimension: MeasurementDimension;
  formatMessage: FormatMessage;
  subjectStats: SubjectStats;
}) => {
  switch (dimension?.sampling) {
    case 'min':
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      return new Date(subjectStats?.minDate).toLocaleDateString();
    case 'max':
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      return new Date(subjectStats?.maxDate).toLocaleDateString();
    case 'first':
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      return new Date(subjectStats?.firstDate).toLocaleDateString();
    case 'last':
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      return new Date(subjectStats?.lastDate).toLocaleDateString();
    default:
      return formatMessage({
        id: 'extractValueDate.noDateAvailable',
        defaultMessage: 'No date available',
      });
  }
};

export const toParsedRangeValue = (value: string | number | null) => {
  if (isNumber(value)) {
    return value;
  }

  if (typeof value === 'string') {
    if (value[0] === '<') return 0;
    if (value[0] === '>') return Number.POSITIVE_INFINITY;
  }
  return null;
};
